import { env } from '../../../lib/env'

const environment = env.NODE_ENV === 'development' ? 'LOCAL' : 'STAGING'
const USER_FIRST_NAME_KEY = `MERU_${environment}_USER_FIRST_NAME`
const USER_LAST_NAME_KEY = `MERU_${environment}_USER_LAST_NAME`
const EMAIL_COUNTER_KEY = `MERU_${environment}_EMAIL_COUNTER`
const EMAIL_COUNTER_DATE = `MERU_${environment}_EMAIL_DATE`
const DEFAULT_FIRST_NAME = 'john'
const DEFAULT_LAST_NAME = 'doe'

export type DevEmailSettings = {
  firstName: string
  lastName: string
  counter: string
}

/**
 * For development purposes only.
 * Get's a unique email address based on the current date and a counter.
 */
export function getDevEmail(): { firstName: string; lastName: string; email: string } {
  const { firstName, lastName, counter } = getDevEmailSettings()
  const counterValue = parseCounter(counter) + 1
  const currentDateString = getCurrentDateString()

  setCookie(EMAIL_COUNTER_KEY, counterValue.toString())
  setCookie(EMAIL_COUNTER_DATE, currentDateString)

  const email = `${firstName}.${lastName}+${currentDateString}-${counterValue}@meruhealth.com`.toLowerCase()

  return { firstName, lastName, email }
}

export function getDevEmailSettings(): DevEmailSettings {
  const firstName = getCookie(USER_FIRST_NAME_KEY) || DEFAULT_FIRST_NAME
  const lastName = getCookie(USER_LAST_NAME_KEY) || DEFAULT_LAST_NAME
  const counter = getCurrentCounter().toString()

  setCookie(USER_FIRST_NAME_KEY, firstName)
  setCookie(USER_LAST_NAME_KEY, lastName)

  return { firstName, lastName, counter }
}

export function setDevEmailFirstName(firstName: string) {
  setCookie(USER_FIRST_NAME_KEY, firstName)
}

export function setDevEmailLastName(lastName: string) {
  setCookie(USER_LAST_NAME_KEY, lastName)
}

export function setDevEmailCounter(counter: string) {
  setCookie(EMAIL_COUNTER_KEY, counter === '' ? '' : parseCounter(counter).toString())
  setCookie(EMAIL_COUNTER_DATE, getCurrentDateString())
}

const getCurrentDateString = () => {
  const currentDate = new Date()
  const day = currentDate.getDate().toString()
  const month = (currentDate.getMonth() + 1).toString()
  const year = currentDate.getFullYear()
  return `${day}.${month}.${year}`
}

const getCurrentCounter = () => {
  const currentDateString = getCurrentDateString()
  const storedDate = getCookie(EMAIL_COUNTER_DATE)

  if (storedDate !== currentDateString) {
    setCookie(EMAIL_COUNTER_KEY, '0')
    setCookie(EMAIL_COUNTER_DATE, currentDateString)
    return 0
  }

  const counter = parseCounter(getCookie(EMAIL_COUNTER_KEY))
  setCookie(EMAIL_COUNTER_KEY, counter.toString())

  return counter
}

const parseCounter = (counter: string | null): number => {
  const counterValue = Number.parseInt(counter || '0', 10)
  return Number.isFinite(counterValue) && counterValue >= 0 ? counterValue : 0
}

const setCookie = (name: string, value: string, days = 30) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  const cookieString = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`
  document.cookie = cookieString
}

const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`
  const cookie = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(nameEQ))

  if (!cookie) {
    return null
  }

  try {
    return decodeURIComponent(cookie.substring(nameEQ.length))
  } catch {
    return cookie.substring(nameEQ.length)
  }
}
